/**
 * Data Access Layer for Task Reminders
 * Supports multiple reminders per task
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type { TaskReminder, TaskReminderCreate, TaskReminderUpdate } from './types.js';

export async function getTaskReminders(taskId: string): Promise<TaskReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.task_reminders)
    .select('*')
    .eq('task_id', taskId)
    .order('offset_hours', { ascending: true });

  if (error) throw error;
  return (data || []) as TaskReminder[];
}

export async function createTaskReminder(input: TaskReminderCreate): Promise<TaskReminder> {
  const { data, error } = await getClient()
    .from(TABLES.task_reminders)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as TaskReminder;
}

export async function updateTaskReminder(id: string, input: TaskReminderUpdate): Promise<TaskReminder> {
  const { data, error } = await getClient()
    .from(TABLES.task_reminders)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as TaskReminder;
}

export async function deleteTaskReminder(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.task_reminders)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deleteTaskReminders(taskId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.task_reminders)
    .delete()
    .eq('task_id', taskId);

  if (error) throw error;
}

/**
 * Get all reminders that need to be sent
 * Note: RLS is disabled, so regular client can access all accounts
 */
export async function getRemindersToSend(): Promise<Array<TaskReminder & { task: any }>> {
  const client = getClient();
  const now = new Date();

  // Get all reminders for tasks that:
  // 1. Have a due_date
  // 2. Are not done or cancelled
  // 3. Have not been sent yet (or were sent more than 24 hours ago)
  const { data, error } = await client
    .from(TABLES.task_reminders)
    .select(`
      *,
      task:task_id (
        id,
        title,
        description,
        type,
        status,
        priority,
        due_date,
        assigned_to_id,
        assigned_to_user_id,
        account_id,
        related_happening_id,
        happening:related_happening_id (
          id,
          title,
          type,
          start_time,
          place_id,
          place:place_id (
            id,
            name,
            address
          )
        )
      )
    `)
    .not('task.due_date', 'is', null)
    .neq('task.status', 'done')
    .neq('task.status', 'cancelled');

  if (error) throw error;

  const reminders = (data || []) as Array<TaskReminder & { task: any }>;

  // Filter reminders that should be sent now
  return reminders.filter((reminder) => {
    if (!reminder.task || !reminder.task.due_date) return false;

    // Parse due_date - if it's already a full ISO string with timezone, use it as-is
    // Otherwise, treat it as UTC (database timestamps should be stored as UTC)
    let dueDate: Date;
    const dueDateStr = reminder.task.due_date;
    if (typeof dueDateStr === 'string') {
      // If the string ends with 'Z' or has timezone offset, parse it as-is
      if (dueDateStr.includes('Z') || dueDateStr.match(/[+-]\d{2}:\d{2}$/)) {
        dueDate = new Date(dueDateStr);
      } else {
        // If no timezone info, treat as UTC by appending 'Z'
        dueDate = new Date(dueDateStr.endsWith('Z') ? dueDateStr : dueDateStr + 'Z');
      }
    } else {
      dueDate = new Date(dueDateStr);
    }
    
    const reminderTime = new Date(dueDate.getTime() - reminder.offset_hours * 60 * 60 * 1000);
    
    // Send if reminder time has passed (within the last hour to avoid missing it)
    const timeDiff = now.getTime() - reminderTime.getTime();
    const shouldSend = timeDiff >= 0 && timeDiff < 60 * 60 * 1000;

    // Debug logging for timezone issues
    if (shouldSend || timeDiff < 3600 * 1000) {
      console.log(`Reminder check: task=${reminder.task.id}, dueDate=${dueDateStr}, parsedDueDate=${dueDate.toISOString()}, reminderTime=${reminderTime.toISOString()}, now=${now.toISOString()}, timeDiff=${Math.round(timeDiff / 1000 / 60)}min, shouldSend=${shouldSend}`);
    }

    // Don't send if already sent in the last 24 hours
    if (reminder.sent_at) {
      const sentAt = new Date(reminder.sent_at);
      const hoursSinceSent = (now.getTime() - sentAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceSent < 24) {
        return false;
      }
    }

    return shouldSend;
  });
}

/**
 * Mark a reminder as sent
 */
export async function markReminderSent(reminderId: string): Promise<void> {
  const client = getClient();
  const { error } = await client
    .from(TABLES.task_reminders)
    .update({ sent_at: new Date().toISOString() })
    .eq('id', reminderId);

  if (error) throw error;
}
